import type { PMIssueActivity, BuiltInIssueStatus } from "@/domains/pm/types";
import type { Translations } from "@/lib/i18n/types";
import { ISSUE_STATUS_CONFIG } from "@/domains/pm/constants";
import { fill } from "@/lib/i18n/fill";

type Language = "en" | "et";

/** dd.mm.yyyy — matches the issue detail panel's date format. */
function formatDate(value: unknown): string {
  if (typeof value !== "string" || !value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${d.getFullYear()}`;
}

/**
 * A status as it was AT THE TIME, which is why this reads the stored key
 * rather than today's catalogue where it can. `resolveLabel` is supplied by
 * callers that have the catalogue in scope, so a custom status reads as its
 * name; without it a custom key prints as itself, which is still truthful.
 */
function statusLabel(
  value: unknown,
  language: Language,
  resolveLabel?: (key: string) => string
): string {
  const cfg = ISSUE_STATUS_CONFIG[value as BuiltInIssueStatus];
  if (cfg) return language === "et" ? cfg.label_et : cfg.label_en;
  if (typeof value === "string" && value) return resolveLabel?.(value) ?? value;
  return "—";
}

/** The item text a checklist row snapshotted into old_value / new_value. */
function itemText(value: unknown): string {
  const text = value && typeof value === "object" ? (value as { text?: unknown }).text : undefined;
  return typeof text === "string" && text ? text : "—";
}

function meta(a: PMIssueActivity, key: string): string | undefined {
  const v = a.metadata?.[key];
  return typeof v === "string" ? v : undefined;
}

/**
 * WHA-3732 / WHA-3733: turn one activity row into a human-readable sentence.
 *
 * Pure + synchronous: every display label (actor name, assignee name,
 * discipline, filename, parent issue number, element name) is snapshotted into
 * the row's jsonb by the DB triggers, so no async hydration or lookup maps are
 * needed. Status and date values are the only things resolved here (status via
 * the i18n status config, dates formatted), since those are language-dependent.
 */
export function renderActivityLine(
  a: PMIssueActivity,
  t: Translations,
  language: Language
): string {
  const act = t.pm.activity;
  const actor = meta(a, "actor_name") || act.system;
  const oldV = a.old_value;
  const newV = a.new_value;

  switch (a.action_type) {
    case "issue_created":
      return fill(act.created, { actor });
    case "issue_deleted":
      return fill(act.deleted, { actor });
    case "issue_restored":
      return fill(act.restored, { actor });
    case "title_changed":
      return fill(act.titleChanged, { actor, new: typeof newV === "string" ? newV : "" });
    case "status_changed":
      return fill(act.statusChanged, {
        actor,
        old: statusLabel(oldV, language),
        new: statusLabel(newV, language),
      });
    case "start_date_changed": {
      const hasOld = typeof oldV === "string" && oldV;
      const hasNew = typeof newV === "string" && newV;
      if (!hasNew) return fill(act.startDateCleared, { actor });
      if (!hasOld) return fill(act.startDateSet, { actor, new: formatDate(newV) });
      return fill(act.startDateChanged, { actor, old: formatDate(oldV), new: formatDate(newV) });
    }
    case "due_date_changed": {
      const hasOld = typeof oldV === "string" && oldV;
      const hasNew = typeof newV === "string" && newV;
      if (!hasNew) return fill(act.dueDateCleared, { actor });
      if (!hasOld) return fill(act.dueDateSet, { actor, new: formatDate(newV) });
      return fill(act.dueDateChanged, { actor, old: formatDate(oldV), new: formatDate(newV) });
    }
    case "parent_changed": {
      const num =
        newV && typeof newV === "object" ? (newV as { issue_number?: number }).issue_number : null;
      if (num == null) return fill(act.parentRemoved, { actor });
      return fill(act.parentSet, { actor, n: String(num) });
    }
    case "assignee_added":
      return fill(act.assigneeAdded, { actor, name: meta(a, "user_name") || "—" });
    case "assignee_removed":
      return fill(act.assigneeRemoved, { actor, name: meta(a, "user_name") || "—" });
    case "discipline_added":
      return fill(act.disciplineAdded, { actor, name: meta(a, "discipline") || "—" });
    case "discipline_removed":
      return fill(act.disciplineRemoved, { actor, name: meta(a, "discipline") || "—" });
    case "document_attached":
      return fill(act.documentAttached, { actor, file: meta(a, "filename") || "—" });
    case "document_detached":
      return fill(act.documentDetached, { actor, file: meta(a, "filename") || "—" });
    case "element_linked":
    case "element_unlinked": {
      const obj = (newV && typeof newV === "object" ? newV : {}) as {
        element_name?: string | null;
        element_guid?: string;
      };
      const label = obj.element_name || obj.element_guid || "—";
      return fill(a.action_type === "element_linked" ? act.elementLinked : act.elementUnlinked, {
        actor,
        element: label,
      });
    }
    // Written by the pm_issue_viewpoints trigger (pipeline migration
    // 20260821104745). The DB records these whether the view was saved from the
    // create dialog or replaced later.
    case "viewpoint_saved":
      return fill(act.viewpointSaved, { actor });
    case "viewpoint_cleared":
      return fill(act.viewpointCleared, { actor });
    case "drawing_anchor_saved":
      return fill(act.drawingAnchorSaved, { actor });
    case "drawing_anchor_cleared":
      return fill(act.drawingAnchorCleared, { actor });
    case "dependency_added":
    case "dependency_removed": {
      const obj = (newV && typeof newV === "object" ? newV : {}) as {
        predecessor_issue_number?: number;
      };
      const n = obj.predecessor_issue_number;
      return fill(
        a.action_type === "dependency_added" ? act.dependencyAdded : act.dependencyRemoved,
        { actor, n: n == null ? "—" : String(n) }
      );
    }
    case "schedule_pin_changed":
      return fill(newV === true ? act.schedulePinned : act.scheduleUnpinned, { actor });
    case "comment_added":
      return fill(act.commentAdded, { actor });
    case "comment_edited":
      return fill(act.commentEdited, { actor });
    // Written by the pm_issue_checklist_items trigger (pipeline migration
    // 20261002083239). The item text is snapshotted, so a deleted or renamed
    // item still reads as it was.
    case "checklist_item_added":
      return fill(act.checklistItemAdded, { actor, item: itemText(newV) });
    case "checklist_item_removed":
      return fill(act.checklistItemRemoved, { actor, item: itemText(oldV) });
    case "checklist_item_renamed":
      return fill(act.checklistItemRenamed, { actor, old: itemText(oldV), new: itemText(newV) });
    case "checklist_item_checked":
      return fill(act.checklistItemChecked, { actor, item: itemText(newV) });
    case "checklist_item_unchecked":
      return fill(act.checklistItemUnchecked, { actor, item: itemText(newV) });
    default:
      return actor;
  }
}
